/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateArticleSentimentV2Config } from 'state/thunks/articlesentimentv2'
// utils
import { createThemeColors, getHueFromHex, isLight } from 'utils/theme'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as ColorIcon } from 'assets/icons/color.svg'
// content
// import Mode from './mode/mode'
import Fonts from './fonts/fonts'
import Colors from './colors/colors';
import Space from 'material/space/space';
import SetTheme from 'material/settheme/settheme';
import ThemePreview from './themepreview/themepreview'
// styles
import injectSheet from 'react-jss'
import styles from './sitethemesection_styles'
import { revertConfigs, setTempArticleSentimentConfig, setTempDisplayConfig, setTempDisplayConfigEmojis } from 'state/slices/articlesentimentv2'
import EmojiSelection from './emojiselection/emojiselection'
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual'
import Language from './language/language'


/* ========== ~~~~~~~~~~ ARTICLE SENTIMENT CONFIG V2: SITE THEME SECTION ~~~~~~~~~~ ========== */
const SiteThemeSection = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const sidebarOpen = useSelector(state => state.app.sidebarOpen)
  const { displayConfig: savedDisplayConfig, articleSentimentUnitId, emojiConfigs: savedEmojiConfigs } = useSelector(state => state.articleSentimentV2.articleSentimentConfig)
  const { displayConfig: tempDisplayConfig, emojiConfigs: tempEmojiConfigs } = useSelector(state => state.articleSentimentV2.tempArticleSentimentConfig)

  const [siteTheme, setSiteTheme] = useState(savedDisplayConfig)

  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    setIsUpdating(true)
    e.stopPropagation()
    const displayConfigRequest = {displayConfig: {...tempDisplayConfig}, emojiConfigs: [...tempEmojiConfigs] }
    const response = await dispatch(updateArticleSentimentV2Config({ data: displayConfigRequest, unitUUID: articleSentimentUnitId, section: 'site-themes' }))
    if(response.payload) setIsOpened(false)
    setIsUpdating(false)
  }, [articleSentimentUnitId, dispatch, tempDisplayConfig, tempEmojiConfigs])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    setSiteTheme(savedDisplayConfig)
    dispatch(revertConfigs({
      configName: 'displayConfig'
    }))
    dispatch(revertConfigs({
      configName: 'emojiConfigs'
    }))
  }, [dispatch, savedDisplayConfig])

  const isChanged = useMemo(() => {
    const isDisplayConfigChanged = !isObjectArrayEqual(savedDisplayConfig, tempDisplayConfig)
    const isEmojiConfigsChanged = !isObjectArrayEqual(savedEmojiConfigs, tempEmojiConfigs)
    return isDisplayConfigChanged || isEmojiConfigsChanged
  }, [savedDisplayConfig, savedEmojiConfigs, tempDisplayConfig, tempEmojiConfigs])

  const themeColors = useMemo(() => createThemeColors(!isLight(siteTheme.customBackgroundColor), getHueFromHex(siteTheme.customPrimaryColor), !!siteTheme.enableMonochrome), [siteTheme])

  const setSiteThemeConfig = useCallback((key, value) => {
    setSiteTheme({...siteTheme, [key]: value})
    dispatch(setTempArticleSentimentConfig({
      configName: 'displayConfig',
      data: {
        ...tempDisplayConfig,
        [key]: value
      }
    }))
  }, [dispatch, siteTheme, tempDisplayConfig])

  const setSiteThemeConfigs = useCallback((configObj) => {
    setSiteTheme({...siteTheme, ...configObj})
    dispatch(setTempArticleSentimentConfig({
      configName: 'displayConfig',
      data: {
        ...tempDisplayConfig,
        ...configObj
      }
    }))
  }, [dispatch, siteTheme, tempDisplayConfig])

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
      saveTitle={'Save Changes'}
      icon={<ColorIcon />}
      title='Site Theme'
    />
  );

  return (
    <ExpandableSection
      className={`expandablesectionv2 sitethemesection ${props.classes.sitethemesection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
    >
      <Space gap={16}>
        <Space klass={`stylesection sidebarOpen-${!!sidebarOpen}`} gap={48} direction={'column'}>
          <Colors 
            isConfigOpen={isOpened}
            siteTheme={siteTheme} 
            setSiteThemeConfig={setSiteThemeConfig} 
            setSiteThemeConfigs={setSiteThemeConfigs}
          />
          <Fonts siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <Language siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <EmojiSelection siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <SetTheme />
        </Space>
        <Space klass={'themesection'} direction={'column'}>
         <ThemePreview siteTheme={siteTheme} themeColors={themeColors} />
        </Space>
      </Space>
      
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default SiteThemeSection;